<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { Lock } from "@element-plus/icons-vue";
import AppHeader from "./components/AppHeader.vue";
import AppSidebar from "./components/AppSidebar.vue";
import SideFloatButtons from "./components/SideFloatButtons.vue";
import ImLinkUI from "@/components/im/ImLinkUI.vue";
import GrowthCenterEntry from "@/components/GrowthCenterEntry.vue";
import FeedbackDialog from "@/components/FeedbackDialog.vue";
import ConvertCash from "@/components/dialogs/ConvertCash.vue";
import IntegralLevel from "@/components/dialogs/IntegralLevel.vue";
import QaTestReport from "@/components/dialogs/QaTestReport.vue";
import ChangeEmail from "@/components/dialogs/ChangeEmail.vue";
import GrowthValuesDialog from "@/components/dialogs/GrowthValuesDialog.vue";
import EnrolmentDialog from "@/components/dialogs/EnrolmentDialog.vue";
import SkypeIdDialog from "@/components/dialogs/SkypeIdDialog.vue";
import AttendanceWarningDialog from "@/components/dialogs/AttendanceWarningDialog.vue";
import ProtocolReminderDialog from "@/components/dialogs/ProtocolReminderDialog.vue";
import BindingPhoneDialog from "@/components/dialogs/BindingPhoneDialog.vue";
import NoticeModal from "@/components/dialogs/NoticeModal.vue";
import SystemMsgModal from "@/components/dialogs/SystemMsgModal.vue";
import CourseAttendanceDialog from "@/components/dialogs/CourseAttendanceDialog.vue";
import ClientDownloadDialog from "@/components/dialogs/ClientDownloadDialog.vue";
import { useLayoutStore } from "@/stores/layout";
import { useUserStore } from "@/stores/user";
import { useTheme } from "@/composables/useTheme";
import { useConfirm } from "@/composables/useFeedback";
import { getFeedBackMsg } from "@/api/feedBack";
import { userExtInfo, isShowDialogList, isShowTutorSystemTransferNotice } from "@/api/teachinfo";
import { getCookie } from "@/utils/tools";

const layoutStore = useLayoutStore();
const userStore = useUserStore();
const router = useRouter();
const { loadSoftMode } = useTheme();
const { alert } = useConfirm();

const MSG_PERSONAL_AND_AGREEMENT = `According to the relevant regulatory authorities, tutors who
provide online teaching services need to submit complete
personal information and the agreement signed with<br>
Acadsoc,so as to better protect the rights of both tutors<br>
and students.<br>
Please complete as soon as possible the personal<br>
information,and sign and upload the agreement;<br>
if you need help, please contact your DS/TM.`;

const MSG_PERSONAL_ONLY = `According to the relevant regulatory authorities, tutors who
provide online teaching services need to submit complete
personal information, so as to better protect the rights
of both tutors and students.<br>
Please complete the personal information as soon as
possible; if you need help, please contact your DS/TM.`;

/** Vue2 adminLayout mounted: incomplete personal info / agreement alert */
async function checkPersonalInfoPrompt() {
  try {
    const res = await userExtInfo();
    const info = res?.data?.Body;
    if (!info) return;

    if (info.ContractType === 1) {
      userStore.setInternationalOrCustom(1);
    }

    // Vue2 precedence: (res && PersonalState==0) || (ContactState==0 && HB==0)
    const needPrompt =
      info.PersonalState == 0 || (info.ContactState == 0 && info.HB == 0);
    if (!needPrompt) return;

    let msg = MSG_PERSONAL_ONLY;
    if (info.HB == 0 && info.TLState == 0) {
      msg = MSG_PERSONAL_AND_AGREEMENT;
    }

    const ok = await alert(msg, {
      type: "default",
      icon: { default: true },
      width: 500,
      confirmButtonText: "Confirm",
    });
    if (!ok) return;

    if (info.PersonalState == 0 && info.ContactState == 0) {
      router.push("/newPersonalData");
    } else if (info.PersonalState == 1 && info.ContactState == 0 && info.HB == 0) {
      // Vue2: contract page navigation was commented out
    } else if (info.PersonalState == 0 && info.ContactState == 1) {
      router.push("/newPersonalData");
    }
  } catch (err) {
    console.warn("userExtInfo personal prompt failed", err);
  }
}

const contentClass = computed(() => (layoutStore.sidebarCollapsed ? "margin-mini" : "margin-full"));

const refreshDialog = ref(false);
/** Vue2 adminLayout skypeIdDialogs */
const skypeIdDialog = ref(false);
/** Vue2 clientDownloadDialog — 系统迁移通知 sort=2 */
const transferNoticeVisible = ref(false);
const transferNoticeTitle = ref("");
const transferNoticeContent = ref("");
const IDLE_MS = 1000 * 60 * 10;

const activityEvents = ["mouseup", "mousemove", "keyup", "click"];
const windowEvents = ["resize", "scroll"];
let idleTimer = null;
let throttleTimer = null;
let throttleStart = Date.now();

function resetIdleTimer() {
  if (idleTimer) clearInterval(idleTimer);
  idleTimer = setInterval(() => {
    refreshDialog.value = true;
  }, IDLE_MS);
}

function throttleHandle() {
  const remaining = 1000 - (Date.now() - throttleStart);
  if (throttleTimer) clearTimeout(throttleTimer);
  if (remaining <= 0) {
    resetIdleTimer();
    throttleStart = Date.now();
  } else {
    throttleTimer = setTimeout(() => {
      resetIdleTimer();
      throttleStart = Date.now();
    }, remaining);
  }
}

function bindIdleWatchers() {
  activityEvents.forEach((name) => document.body.addEventListener(name, throttleHandle));
  windowEvents.forEach((name) => window.addEventListener(name, throttleHandle));
}

function unbindIdleWatchers() {
  activityEvents.forEach((name) => document.body.removeEventListener(name, throttleHandle));
  windowEvents.forEach((name) => window.removeEventListener(name, throttleHandle));
}

function closeIdleDialog() {
  refreshDialog.value = false;
}

function toRefresh() {
  refreshDialog.value = false;
  window.location.reload();
}

/** Vue2 adminLayout.isShowDialogList — Type=="1" opens Skype ID dialog */
async function checkSkypeIdDialog() {
  try {
    const res = await isShowDialogList();
    if (res?.data?.ErrorCode == 0 && res.data.Body?.data?.length > 0) {
      const list = res.data.Body.data.filter((item) => item.Type == "1");
      skypeIdDialog.value = list.length > 0;
    } else {
      skypeIdDialog.value = false;
    }
  } catch (_) {
    skypeIdDialog.value = false;
  }
}

/** Vue2 adminLayout isShowTutorSystemTransferNotice — 非客户端才弹 */
async function checkTransferNotice() {
  if (getCookie("client") === "tutorclient") return;
  try {
    const res = await isShowTutorSystemTransferNotice();
    const body = res?.data?.Body;
    if (res?.data?.ErrorCode === 0 && body?.ShowNotice) {
      transferNoticeTitle.value = body.Title || "";
      transferNoticeContent.value = body.Content || "";
      transferNoticeVisible.value = true;
    }
  } catch (_) {
    /* ignore */
  }
}

onMounted(() => {
  loadSoftMode();
  getFeedBackMsg()
    .then((res) => userStore.setFeedBackMsg(!!res?.data?.Body))
    .catch(() => {});
  checkPersonalInfoPrompt();
  checkSkypeIdDialog();
  checkTransferNotice();
  resetIdleTimer();
  bindIdleWatchers();
});

onUnmounted(() => {
  if (idleTimer) clearInterval(idleTimer);
  if (throttleTimer) clearTimeout(throttleTimer);
  unbindIdleWatchers();
});
</script>

<template>
  <div class="admin-layout">
    <AppHeader />
    <div class="page-content">
      <AppSidebar />
      <main class="common-content" :class="contentClass">
        <router-view />
      </main>
    </div>
    <SideFloatButtons />
    <ImLinkUI />
    <GrowthCenterEntry />
    <FeedbackDialog />
    <ConvertCash />
    <IntegralLevel />
    <QaTestReport />
    <ChangeEmail />
    <GrowthValuesDialog />
    <EnrolmentDialog />
    <SkypeIdDialog v-model="skypeIdDialog" />
    <AttendanceWarningDialog />
    <ProtocolReminderDialog />
    <BindingPhoneDialog />
    <NoticeModal />
    <SystemMsgModal />
    <CourseAttendanceDialog />
    <ClientDownloadDialog
      v-model="transferNoticeVisible"
      :sort="2"
      :title="transferNoticeTitle"
      :content="transferNoticeContent"
    />

    <el-dialog
      v-model="refreshDialog"
      width="500px"
      align-center
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      :show-close="false"
      class="idle-refresh-dialog"
      @closed="closeIdleDialog"
    >
      <div class="idle-refresh-body">
        <el-icon class="idle-lock" :size="36"><Lock /></el-icon>
        <p class="idle-title">Long time no operation.</p>
        <p class="idle-desc">Please refresh the page to view the latest data!</p>
        <button type="button" class="idle-refresh-btn" @click="toRefresh">Refresh</button>
      </div>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.admin-layout {
  /* 相对 #app 高度，不用 100vh：Qt/TutorClient WebView 的 vh 常大于可视区 */
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--app-bg, $common-background);
  font-family: $font-family-base;
  color: var(--app-text, $blue-title);
}

.page-content {
  flex: 1;
  min-height: 0;
  padding-top: $layout-header-height;
  display: flex;
  overflow: hidden;
  background: var(--app-bg, $common-background);
}

.common-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #fff;
  /* 课表等页 min-width 超出时在此横向滚动，避免内容被挤挡 */
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  transition:
    margin-left 0.2s ease,
    max-width 0.2s ease;
}

[data-theme="theme1"] .common-content {
  background: #151827;
}

.margin-full {
  margin-left: $layout-sidebar-width;
  max-width: calc(100vw - #{$layout-sidebar-width});
}

.margin-mini {
  margin-left: $layout-sidebar-mini-content-offset;
  max-width: calc(100vw - #{$layout-sidebar-mini-content-offset});
}
</style>

<style lang="scss">
.idle-refresh-dialog.el-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(50, 57, 122, 0.18);
}

.idle-refresh-dialog .el-dialog__header {
  display: none;
}

.idle-refresh-dialog .el-dialog__body {
  padding: 28px 24px 24px;
}

.idle-refresh-body {
  text-align: center;
}

.idle-lock {
  color: #ff9991;
}

.idle-title {
  margin: 12px 0 8px;
  color: #ff9991;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.4;
  font-weight: bold;
}

.idle-desc {
  margin: 0 0 20px;
  color: #32397a;
  font-size: 16px;
  line-height: 1.5;
}

[data-theme="theme1"] .idle-desc {
  color: #ececec;
}

.idle-refresh-btn {
  appearance: none;
  border: 0;
  min-width: 88px;
  height: 28px;
  padding: 0 18px;
  border-radius: 4px;
  background: #ff9991;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  line-height: 28px;
}
</style>
